import React, { useState } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { TicketPercent, X, Loader2 } from 'lucide-react';

// Coupon entry + applied-state chip. Validates against the backend with the
// live subtotal; parent receives {code, discount, message} or null.
export const CouponBox = ({ subtotal, coupon, onChange }) => {
  const [code, setCode] = useState('');
  const [checking, setChecking] = useState(false);

  const apply = async (rawCode) => {
    const c = (rawCode ?? code).trim().toUpperCase();
    if (!c) return;
    setChecking(true);
    try {
      const r = await api.post('/coupons/validate', { code: c, subtotal });
      onChange({ code: r.data.code, discount: r.data.discount, message: r.data.message });
      toast.success(`Coupon ${r.data.code} applied — ${r.data.message}`);
      setCode('');
    } catch (e) {
      onChange(null);
      toast.error(e.response?.data?.detail || 'That coupon code isn\u2019t valid');
    } finally {
      setChecking(false);
    }
  };

  if (coupon) {
    return (
      <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="coupon-applied">
        <div className="flex items-center gap-2 text-sm text-emerald-800">
          <TicketPercent className="h-4 w-4" />
          <code className="font-mono font-bold">{coupon.code}</code>
          <span>· {coupon.message}</span>
        </div>
        <button onClick={() => onChange(null)} className="text-emerald-700 hover:text-emerald-900" aria-label="Remove coupon" data-testid="coupon-remove">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <Input
        value={code}
        onChange={e => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
        onKeyDown={e => { if (e.key === 'Enter') apply(); }}
        placeholder="Coupon code"
        className="rounded-xl font-mono"
        data-testid="coupon-input"
      />
      <Button variant="outline" onClick={() => apply()} disabled={checking || !code.trim()} className="rounded-xl shrink-0" data-testid="coupon-apply-btn">
        {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
      </Button>
    </div>
  );
};

// Auto-apply helper for ?coupon=CODE links (Facebook promos)
export const autoApplyCoupon = async (codeFromUrl, subtotal, onChange) => {
  if (!codeFromUrl || subtotal <= 0) return;
  try {
    const r = await api.post('/coupons/validate', { code: codeFromUrl, subtotal });
    onChange({ code: r.data.code, discount: r.data.discount, message: r.data.message });
    toast.success(`Coupon ${r.data.code} applied — ${r.data.message}`);
  } catch (e) {
    toast.error(e.response?.data?.detail || 'The coupon in your link isn\u2019t valid');
  }
};
